import React, { useState, useEffect } from 'react';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-enterprise';
import 'ag-grid-community/dist/styles/ag-grid.css';
import 'ag-grid-community/dist/styles/ag-theme-balham.css';
import colDef from '../../../data/grid/coldefManageConditionCustomize';
import commonColDef from '../../../data/grid/commonColDef';
import SelectConditionCustomizeRenderer from '../../cellRenderers/SelectConditionCustomizeRenderer';
import useGridOptionsConditionCustomize from '../../../hooks/grid/gridOptions/useGridOptionsConditionCustomize';
import useColdefConditionCustomizeDomesticNsc from '../../../hooks/grid/coldef/useColdefConditionCustomizeDomesticNsc';
import useColdefConditionCustomizeDomesticAichi from '../../../hooks/grid/coldef/useColdefConditionCustomizeDomesticAichi';
import useColdefConditionCustomizeDomesticKobe from '../../../hooks/grid/coldef/useColdefConditionCustomizeDomesticKobe';
import useColdefConditionCustomizeDomesticJfe from '../../../hooks/grid/coldef/useColdefConditionCustomizeDomesticJfe';
import useColdefConditionCustomizeExportNsc from '../../../hooks/grid/coldef/useColdefConditionCustomizeExportNsc';
import useColdefConditionCustomizeExportAichi from '../../../hooks/grid/coldef/useColdefConditionCustomizeExportAichi';
import useColdefConditionCustomizeExportKobe from '../../../hooks/grid/coldef/useColdefConditionCustomizeExportKobe';
import useColdefConditionCustomizeExportJfe from '../../../hooks/grid/coldef/useColdefConditionCustomizeExportJfe';

import { useTranslation } from 'react-i18next';
import { BUSINESS_TYPE, CONDITION_VIEW_MODES, STEEL_MAKER } from '../../../constants';
import type { ColDef, ColumnApi, GridApi, GridReadyEvent } from 'ag-grid-community';
import type { SteelMaker, BusinessType } from '../../../types/common';
import type { ConditionWithDetailTypeClient, ConditionViewMode } from '../../../types/condition';

interface GridConditionCustomizeProps {
  isLoading: boolean;
  viewMode: ConditionViewMode;
  steelMaker: SteelMaker;
  businessType: BusinessType;
  condition: ConditionWithDetailTypeClient;
}

const GridConditionCustomize = ({ isLoading, condition, viewMode, steelMaker, businessType }: GridConditionCustomizeProps): React.ReactElement => {
  const { t } = useTranslation();
  const [gridApi, setGridApi] = useState<GridApi | undefined>(undefined);
  const [gridColumnApi, setGridColumnApi] = useState<ColumnApi | undefined>(undefined);
  const { gridOptions } = useGridOptionsConditionCustomize();
  const customColDef: ColDef = {
    cellClass: 'cell',
  };
  const defaultColDef: ColDef = Object.assign({}, commonColDef, customColDef);
  const [columnDefs, setColumnDefs] = useState<ColDef[]>(colDef);
  const colDefDomesticNsc: ColDef[] = useColdefConditionCustomizeDomesticNsc(t);
  const colDefDomesticAichi: ColDef[] = useColdefConditionCustomizeDomesticAichi(t);
  const colDefDomesticKobe: ColDef[] = useColdefConditionCustomizeDomesticKobe(t);
  const colDefDomesticJfe: ColDef[] = useColdefConditionCustomizeDomesticJfe(t);
  const colDefExportNsc: ColDef[] = useColdefConditionCustomizeExportNsc(t);
  const colDefExportAichi: ColDef[] = useColdefConditionCustomizeExportAichi(t);
  const colDefExportKobe: ColDef[] = useColdefConditionCustomizeExportKobe(t);
  const colDefExportJfe: ColDef[] = useColdefConditionCustomizeExportJfe(t);
  const frameworkComponents = {
    selectConditionCustomizeRenderer: SelectConditionCustomizeRenderer,
  };

  // SIDE EFFECT
  // ================================================================================

  // 確認用
  // useEffect(() => {
  //   // console.log('changed');
  //   if(gridApi) {
  //     const displayedData = [];
  //     gridApi?.forEachNode((node) => {
  //       const data = node.data;
  //       displayedData.push(data);
  //     });
  //     // console.log(displayedData)
  //   }
  // }, [gridApi, condition?.detailCustomize]);

  /**
   * 受け取ったmaker, typeに応じてColDefを変更する
   */
  useEffect(() => {
    if (businessType === BUSINESS_TYPE.DOMESTIC) {
      // 国内
      if (steelMaker === STEEL_MAKER.NIPPONSTEEL) {
        setColumnDefs(colDefDomesticNsc);
      } else if (steelMaker === STEEL_MAKER.AICHISTEEL) {
        setColumnDefs(colDefDomesticAichi);
      } else if (steelMaker === STEEL_MAKER.KOBESTEEL) {
        setColumnDefs(colDefDomesticKobe);
      } else if (steelMaker === STEEL_MAKER.JFESTEEL) {
        setColumnDefs(colDefDomesticJfe);
      } else {
        setColumnDefs(colDef);
      }
    } else if (businessType === BUSINESS_TYPE.EXPORT) {
      // 輸出
      if (steelMaker === STEEL_MAKER.NIPPONSTEEL) {
        setColumnDefs(colDefExportNsc);
      } else if (steelMaker === STEEL_MAKER.AICHISTEEL) {
        setColumnDefs(colDefExportAichi);
      } else if (steelMaker === STEEL_MAKER.KOBESTEEL) {
        setColumnDefs(colDefExportKobe);
      } else if (steelMaker === STEEL_MAKER.JFESTEEL) {
        setColumnDefs(colDefExportJfe);
      } else {
        setColumnDefs(colDef);
      }
    } else {
      setColumnDefs(colDef);
    }
  }, [steelMaker, businessType]);

  /**
   * Loading, NoRowsのグリッド表示
   */
  useEffect(() => {
    if (gridApi) {
      if (isLoading) {
        gridApi.showLoadingOverlay();
      } else {
        if (viewMode === CONDITION_VIEW_MODES.EDITING && condition?.detailCustomize?.length) {
          gridApi.hideOverlay();
        } else if (viewMode === CONDITION_VIEW_MODES.APPROVED && condition?.detailCustomizeApprove?.length) {
          gridApi.hideOverlay();
        } else {
          gridApi.showNoRowsOverlay();
        }
      }
    } else {
      // do nothing
    }
  }, [gridApi, isLoading, viewMode, condition?.detailCustomize, condition?.detailCustomizeApprove]);

  // EVENT HANDLER
  // ================================================================================

  /**
   * グリッドが準備できたらAPIを利用できるようにする
   * @param params GridReadyEvent
   */
  const onGridReady = (params: GridReadyEvent) => {
    // console.log('condition customize grid ready');
    setGridApi(params.api);
    setGridColumnApi(params.columnApi);
  };

  // VIEW
  // ================================================================================

  return (
    <>
      <div id="agGridConditionCustomize" className="ag-theme-balham mt-4" style={{ height: viewMode === CONDITION_VIEW_MODES.APPROVED ? '50vh' : '30vh' }}>
        <AgGridReact
          key={'ag-grid-' + viewMode.toLowerCase()}
          immutableData={true}
          gridOptions={gridOptions}
          defaultColDef={defaultColDef}
          columnDefs={columnDefs}
          rowData={condition ? (viewMode === CONDITION_VIEW_MODES.APPROVED ? condition.detailCustomizeApprove : condition.detailCustomize) : []}
          onGridReady={onGridReady}
          frameworkComponents={frameworkComponents}
        />
      </div>
    </>
  );
};

export default GridConditionCustomize;
